import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Wifi, WifiOff, ArchiveRestore, Archive, Search } from 'lucide-react';
import { DEVICE_TYPES, CONNECTION_TYPES, deviceTypeInfo, connectionTypeLabel } from '../utils/deviceConfig';

export default function Devices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    display_name: '', customer_id: '', device_type: 'computer', device_subtype: '',
    is_multifunction: false, connection_type: '', location: '', serial_number: '',
    notes: '', purchase_date: '', ip_address: '',
  });

  const customerId = searchParams.get('customer_id') || '';
  const deviceType = searchParams.get('device_type') || '';
  const showArchived = searchParams.get('is_active') === 'false';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customerId) params.set('customer_id', customerId);
      if (deviceType) params.set('device_type', deviceType);
      params.set('is_active', showArchived ? 'false' : 'true');
      const res = await api.get('/devices?' + params.toString());
      setDevices(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Geraete');
    } finally {
      setLoading(false);
    }
  }, [customerId, deviceType, showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/customers?limit=200').then(res => setCustomers(res.data.customers || res.data || [])).catch(() => {});
  }, []);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const handleCreate = async () => {
    if (!form.display_name || !form.device_type) {
      toast.error('Name und Geraetetyp sind erforderlich');
      return;
    }
    setSaving(true);
    try {
      await api.post('/devices', {
        ...form,
        customer_id: form.customer_id ? parseInt(form.customer_id) : null,
        device_subtype: form.device_type === 'printer' ? (form.device_subtype || null) : null,
      });
      toast.success('Geraet angelegt');
      setShowForm(false);
      setForm({ display_name: '', customer_id: '', device_type: 'computer', device_subtype: '', is_multifunction: false, connection_type: '', location: '', serial_number: '', notes: '', purchase_date: '', ip_address: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const filtered = devices.filter(d => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (d.display_name || '').toLowerCase().includes(q)
      || (d.system_name || '').toLowerCase().includes(q)
      || (d.customer_name || '').toLowerCase().includes(q)
      || (d.serial_number || '').toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Geraete</h2>
          <p className="text-sm text-gray-500 mt-1">{filtered.length} {showArchived ? 'archiviert' : 'aktiv'}</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
          <Plus size={16} /> Geraet hinzufuegen
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-1">Neues Fremdgeraet anlegen</h3>
          <p className="text-xs text-gray-400 mb-3">Fuer Geraete, die nicht ueber NinjaOne verwaltet werden. Ninja-Geraete kommen automatisch ueber den Sync rein.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input type="text" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kunde</label>
              <select value={form.customer_id} onChange={e => setForm({ ...form, customer_id: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Kein Kunde</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Geraetetyp *</label>
              <select value={form.device_type} onChange={e => setForm({ ...form, device_type: e.target.value, device_subtype: '' })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                {DEVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {form.device_type === 'printer' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Drucker-Subtyp</label>
                <select value={form.device_subtype} onChange={e => setForm({ ...form, device_subtype: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">-- waehlen --</option>
                  <option value="laser_bw">Laser Schwarzweiss</option>
                  <option value="laser_color">Laser Farbe</option>
                  <option value="inkjet">Tintenstrahl</option>
                  <option value="label">Etikettendrucker</option>
                  <option value="dot_matrix">Nadeldrucker</option>
                  <option value="3d_printer">3D-Drucker</option>
                </select>
              </div>
            )}
            {form.device_type === 'printer' && (
              <div className="flex items-center gap-2 mt-5">
                <input type="checkbox" id="is_multifunction" checked={form.is_multifunction}
                  onChange={e => setForm({ ...form, is_multifunction: e.target.checked })} className="rounded border-gray-300" />
                <label htmlFor="is_multifunction" className="text-sm text-gray-700">Multifunktionsgeraet (MFC)</label>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Verbindung</label>
              <select value={form.connection_type} onChange={e => setForm({ ...form, connection_type: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">-- waehlen --</option>
                {CONNECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Standort</label>
              <input type="text" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Seriennummer</label>
              <input type="text" value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">IP-Adresse</label>
              <input type="text" value={form.ip_address} onChange={e => setForm({ ...form, ip_address: e.target.value })}
                placeholder="192.168.1.50"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Kaufdatum</label>
              <input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Notizen</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-3">
            <button onClick={handleCreate} disabled={saving}
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
              {saving ? 'Wird gespeichert...' : 'Anlegen'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..."
            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
        </div>
        <select value={customerId} onChange={e => updateFilter('customer_id', e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
          <option value="">Alle Kunden</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={deviceType} onChange={e => updateFilter('device_type', e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white">
          <option value="">Alle Typen</option>
          {DEVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <button onClick={() => updateFilter('is_active', showArchived ? '' : 'false')}
          className={'flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-xl border transition-colors ' +
            (showArchived ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50')}>
          {showArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          {showArchived ? 'Archivierte' : 'Aktive'}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
        {loading && <p className="text-sm text-gray-400 p-4">Laden...</p>}
        {!loading && filtered.length === 0 && <p className="text-sm text-gray-400 p-4">Keine Geraete gefunden</p>}
        {!loading && filtered.map(d => {
          const typeInfo = deviceTypeInfo(d.device_type);
          const Icon = typeInfo.icon;
          const isNinja = !d.is_third_party;
          return (
            <Link key={d.id} to={'/devices/' + d.id}
              className={'flex items-center justify-between px-4 py-3 transition-colors ' +
                (d.not_our_responsibility ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50')}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-gray-100 rounded-lg p-2 shrink-0">
                  <Icon size={16} className="text-gray-500" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {d.display_name || d.system_name || d.dns_name || ('Geraet ' + d.id)}
                    </p>
                    {d.is_third_party && (
                      <span className="text-[10px] font-medium bg-fuchsia-50 text-fuchsia-600 px-1.5 py-0.5 rounded-full shrink-0">Fremdgeraet</span>
                    )}
                    {d.not_our_responsibility && (
                      <span className="text-[10px] font-medium bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full shrink-0">Nicht betreut</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    {typeInfo.label}
                    {d.customer_name ? ' · ' + d.customer_name : ''}
                    {d.connection_type ? ' · ' + connectionTypeLabel(d.connection_type) : ''}
                  </p>
                </div>
              </div>
              {isNinja && (
                <span className={'text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ' + (d.offline ? 'bg-red-50 text-red-500' : 'bg-teal-50 text-teal-600')}>
                  {d.offline ? <WifiOff size={11} className="inline mr-1" /> : <Wifi size={11} className="inline mr-1" />}
                  {d.offline ? 'Offline' : 'Online'}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
